import { atom } from 'jotai';
import type { PBRaceRecord } from '../api/pbTypes.ts';
import type { BracketNodeDefinition } from '../bracket/doubleElimDefinition.ts';
import type { BracketDiagramViewModel } from '../bracket/eliminationState.ts';
import { bracketDiagramAtom, bracketEnabledAtom } from '../bracket/eliminationState.ts';
import { nextRacesAtom } from './race-atoms.ts';

export interface NextRaceEntry {
	raceId: string;
	race: PBRaceRecord | null;
	definition: BracketNodeDefinition | null;
	isPredicted: boolean;
}

const MAX_NEXT_RACES = 8;

export function buildNextRaceEntries(
	nextRaces: PBRaceRecord[],
	diagram: BracketDiagramViewModel,
	maxEntries: number = MAX_NEXT_RACES,
): NextRaceEntry[] {
	const entries: NextRaceEntry[] = [];
	const seenIds = new Set<string>();

	for (const race of nextRaces) {
		const node = diagram.nodes.find((n) => n.race?.id === race.id) ?? null;
		entries.push({
			raceId: race.id,
			race,
			definition: node?.definition ?? null,
			isPredicted: false,
		});
		seenIds.add(race.id);
		if (entries.length >= maxEntries) {
			return entries;
		}
	}

	const sequencePositions = new Map<number, number[]>();
	for (const [index, order] of (diagram.runSequence ?? []).entries()) {
		const bucket = sequencePositions.get(order) ?? [];
		bucket.push(index);
		sequencePositions.set(order, bucket);
	}

	const getNextSequenceIndex = (order: number, assignedHeatCount: number): number => {
		const positions = sequencePositions.get(order);
		if (!positions || positions.length === 0) return Number.POSITIVE_INFINITY;
		return positions[assignedHeatCount] ?? Number.POSITIVE_INFINITY;
	};

	const predictedNodes = diagram.nodes
		.filter((node) =>
			!node.race &&
			node.slots.some((slot) => slot.isPredicted)
		)
		.sort((a, b) => {
			const aSeq = getNextSequenceIndex(a.definition.order, a.assignedHeatCount);
			const bSeq = getNextSequenceIndex(b.definition.order, b.assignedHeatCount);
			if (aSeq !== bSeq) return aSeq - bSeq;
			return a.definition.order - b.definition.order;
		});

	for (const node of predictedNodes) {
		if (entries.length >= maxEntries) break;
		const predictedId = `predicted-race-${node.definition.order}`;
		if (seenIds.has(predictedId)) continue;
		entries.push({
			raceId: predictedId,
			race: null,
			definition: node.definition,
			isPredicted: true,
		});
		seenIds.add(predictedId);
	}

	return entries;
}

export const nextRaceEntriesAtom = atom((get): NextRaceEntry[] => {
	const nextRaces = get(nextRacesAtom);
	const diagram = get(bracketDiagramAtom);
	if (!get(bracketEnabledAtom)) {
		return nextRaces.slice(0, MAX_NEXT_RACES).map((race) => ({
			raceId: race.id,
			race,
			definition: null,
			isPredicted: false,
		}));
	}
	return buildNextRaceEntries(nextRaces, diagram, MAX_NEXT_RACES);
});
